import "./MobileOffers.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { authApi, offersApi, setAuthToken } from "../../../api";
import usePullToRefresh from "../../../hooks/usePullToRefresh";

const SAMPLE_OFFERS = [
  { id: "sample-1", title: "Hexa Garden Stack", reward: "20.8Mn Coin", icon: "https://placehold.co/96x96/png", duration: "1 day", category: "Multi-Reward • Gaming", tagline: "Register Now" },
  { id: "sample-2", title: "Color Brick Stack", reward: "10.7Mn Coin", icon: "https://placehold.co/96x96/png", duration: "5 hrs", category: "Multi-Reward • Puzzle", tagline: "Register Now" },
  { id: "sample-3", title: "Color Bolts Sort Puzzle", reward: "10.5Mn Coin", icon: "https://placehold.co/96x96/png", duration: "1 day", category: "Multi-Reward • Gaming", tagline: "Register Now" },
  { id: "sample-4", title: "Hoop Stack 3D – Color Sort", reward: "10.7Mn Coin", icon: "https://placehold.co/96x96/png", duration: "7 hrs", category: "Multi-Reward • Casual", tagline: "Register Now" },
  { id: "sample-5", title: "Deluxe Block Jewel", reward: "9.8Mn Coin", icon: "https://placehold.co/96x96/png", duration: "1 hr", category: "Puzzle • Reward", tagline: "Register Now" },
  { id: "sample-6", title: "Idle Heroes Merge", reward: "8.3Mn Coin", icon: "https://placehold.co/96x96/png", duration: "1 day", category: "Idle • RPG", tagline: "New" },
  { id: "sample-7", title: "Candy Coin Frenzy", reward: "7.9Mn Coin", icon: "https://placehold.co/96x96/png", duration: "Instant", category: "Casual • Reward", tagline: "Hot offer" },
  { id: "sample-8", title: "Word Dash Classic", reward: "6.5Mn Coin", icon: "https://placehold.co/96x96/png", duration: "12 hrs", category: "Trivia • Reward", tagline: "Register Now" },
  { id: "sample-9", title: "Merge Farm Mania", reward: "5.2Mn Coin", icon: "https://placehold.co/96x96/png", duration: "Instant", category: "Simulation • Reward", tagline: "Register Now" },
  { id: "sample-10", title: "Royal Slots Journey", reward: "4.9Mn Coin", icon: "https://placehold.co/96x96/png", duration: "18 hrs", category: "Casino • Reward", tagline: "Register Now" },
  { id: "sample-11", title: "Tap Coin Rush", reward: "4.1Mn Coin", icon: "https://placehold.co/96x96/png", duration: "Instant", category: "Casual • Tap", tagline: "New" },
  { id: "sample-12", title: "Adventures of Nova", reward: "3.7Mn Coin", icon: "https://placehold.co/96x96/png", duration: "2 days", category: "Adventure • Reward", tagline: "Register Now" },
  { id: "sample-13", title: "Crypto Clicker Pro", reward: "3.2Mn Coin", icon: "https://placehold.co/96x96/png", duration: "Instant", category: "Finance • Reward", tagline: "Register Now" },
  { id: "sample-14", title: "Galaxy Puzzle Quest", reward: "2.9Mn Coin", icon: "https://placehold.co/96x96/png", duration: "9 hrs", category: "Puzzle • Reward", tagline: "Register Now" },
  { id: "sample-15", title: "Farming Empire Tycoon", reward: "2.4Mn Coin", icon: "https://placehold.co/96x96/png", duration: "3 days", category: "Simulation • Reward", tagline: "Register Now" },
];
const SAMPLE_APPS = [{ name: "Givvy Offerwall", displayName: "Givvy Offerwall" }];
const DEFAULT_APP_DESCRIPTION = "Complete verified offers and collect rewards instantly.";
const HEADER_TITLE = "Givvy Offerwall";
const CARD_CATEGORY = "Multi rewards";

const isOfferActive = (offer) => {
  if (!offer) return false;
  if (typeof offer.active === "boolean") return offer.active;
  const status = (offer.status || offer.offerStatus || "").toString().toLowerCase();
  if (status) return status === "active";
  return true;
};

const mapAppToOffer = (app) => {
  if (!app) return null;
  const rewards = Array.isArray(app.rewards)
    ? app.rewards.map((item) => ({
      label: item.name || item.label || "Reward",
      value: String(item.amount ?? item.value ?? item.points ?? "0"),
    }))
    : undefined;

  return {
    ...app,
    id: app.packageName || app.appName || app.appId || app.id,
    title: app.displayName || app.appName || app.name || app.title,
    packageName: app.packageName || app.appId || app.id,
    icon: app.logo,
    reward: app.rewardTitle ?? app.reward,
    description: app.offerLongDesc || app.description,
    rewardConditions: app.rewardConditions,
    support: app.supportText || app.support,
    tagline: app.offerShortDesc || app.tagline,
    category: app.category || CARD_CATEGORY,
    duration: app.duration,
    rewards,
  };
};

function useQueryParams() {
  if (typeof window === "undefined") {
    return {
      userId: "",
      siteUserId: "",
      packageName: "",
      apiKey: "",
    };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    userId: params.get("userId") || "",
    siteUserId: params.get("siteUserId") || "",
    packageName: params.get("packageName") || "",
    apiKey: params.get("apiKey") || "",
  };
}

function normalizeAppsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.result?.apps)) return payload.result.apps;
  if (Array.isArray(payload.apps)) return payload.apps;
  return [];
}

function normalizeActiveApps(payload) {
  const raw = payload?.result?.activeApps;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") return { appName: item };
      if (typeof item === "object") return item;
      return null;
    })
    .filter(Boolean);
}

export default function MobileOffers({ onSelectOffer = () => { }, removedOfferKeys = [] }) {
  usePullToRefresh();
  const { userId, siteUserId, packageName, apiKey } = useQueryParams();
  const [offers, setOffers] = useState([]);
  const [apps, setApps] = useState([]);
  const [activeSegment, setActiveSegment] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authTokenState, setAuthTokenState] = useState();
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [activeApps, setActiveApps] = useState([]);
  const [pendingOfferId, setPendingOfferId] = useState(null);
  const scrollPositionsRef = useRef({});
  const activeSegmentRef = useRef(activeSegment);
  const hasRestoredScrollRef = useRef(false);

  useEffect(() => {
    if (!apiKey) {
      setAuthToken("");
      setAuthTokenState(undefined);
      return;
    }
    let canceled = false;
    const doLogin = async () => {
      setError("");
      try {
        console.log("mobileOffers /auth/login start", { apiKey });
        const response = await authApi.login({ apiKey });
        if (canceled) return;
        const token = response?.token ?? response?.result?.token ?? "";
        console.log("mobileOffers /auth/login success", { token, response });
        setAuthToken(token);
        setAuthTokenState(token);
        try {
          window.sessionStorage.setItem("offerwallAuthToken", token);
        } catch (err) {
          console.warn("mobileOffers token persist failed", err);
        }
        const user = response?.user || response?.result?.user || null;
        setLoggedInUser(user);
        return;
      } catch (err) {
        if (!canceled) {
          console.warn("mobileOffers /auth/login failed", err);
          setError(err.message || "Invalid API key.");
          setAuthToken("");
          setAuthTokenState(null);
          setLoggedInUser(null);
        }
      }
    };
    doLogin();
    return () => { canceled = true; };
  }, [apiKey]);

  useEffect(() => {
    if (apiKey && authTokenState === undefined) return;
    let canceled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        console.log("mobileOffers /getOfferwallConfig start", { userId, packageName, token: authTokenState });
        const offersRes = await offersApi.getAllAppsForUser({ userId, siteUserId, packageName });
        console.log("mobileOffers /getOfferwallConfig response", offersRes);
        if (canceled) return;
        const parsedOffers = Array.isArray(offersRes) ? offersRes : offersRes?.result?.apps || [];
        const normalizedApps = normalizeAppsPayload(offersRes);
        const appOffers = normalizedApps.map(mapAppToOffer).filter(Boolean);
        const finalOffers = appOffers.length
          ? appOffers
          : parsedOffers.length
            ? parsedOffers
            : SAMPLE_OFFERS;
        setOffers(finalOffers);
        setApps(normalizedApps.length ? normalizedApps : SAMPLE_APPS);
        setActiveApps(normalizeActiveApps(offersRes));
      } catch (err) {
        if (!canceled) {
          setError(err.message || "Unable to load offers.");
          setOffers(SAMPLE_OFFERS);
          setApps(SAMPLE_APPS);
          setActiveApps([]);
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    }
    load();
    return () => { canceled = true; };
  }, [userId, packageName, apiKey, authTokenState]);

  const segmentOptions = [
    { id: "all", label: "All offers" },
    { id: "active", label: "In progress" },
  ];

  const visibleOffers = useMemo(() => {
    const removedSet = new Set(removedOfferKeys);
    const activeSet = new Set(
      activeApps.flatMap((app) => ([
        app.appName,
        app.displayName,
        app.packageName,
        app.appId,
        app.id,
        app.name,
        app.title,
      ]))
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase())
    );
    const markActive = (offer) => {
      if (!offer) return offer;
      const keys = [
        offer.appName,
        offer.displayName,
        offer.title,
        offer.name,
        offer.packageName,
        offer.appId,
        offer.id,
        offer.app?.appName,
        offer.app?.displayName,
        offer.app?.name,
        offer.app?.packageName,
        offer.app?.appId,
        offer.app?.id,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
      const isActiveOffer = keys.some((key) => activeSet.has(key));
      return isActiveOffer ? { ...offer, isActiveOffer: true } : offer;
    };
    if (activeSegment === "active") {
      if (!activeApps.length) return [];
      const offerIndex = new Map();
      const indexOffer = (offer) => {
        [
          offer.appName,
          offer.displayName,
          offer.title,
          offer.name,
          offer.packageName,
          offer.appId,
          offer.id,
        ]
          .filter(Boolean)
          .forEach((value) => {
            offerIndex.set(String(value).trim().toLowerCase(), offer);
          });
      };
      offers.forEach(indexOffer);
      const normalizedActive = activeApps.map(mapAppToOffer).filter(Boolean);
      const merged = normalizedActive.map((activeOffer) => {
        const keys = [
          activeOffer.appName,
          activeOffer.displayName,
          activeOffer.title,
          activeOffer.name,
          activeOffer.packageName,
          activeOffer.appId,
          activeOffer.id,
        ]
          .filter(Boolean)
          .map((value) => String(value).trim().toLowerCase());
        const matched = keys.map((key) => offerIndex.get(key)).find(Boolean);
        return matched ? { ...activeOffer, ...matched, isActiveOffer: true } : { ...activeOffer, isActiveOffer: true };
      });
      const filtered = merged.length
        ? merged
        : offers.filter(isOfferActive).map((offer) => ({ ...offer, isActiveOffer: true }));
      return filtered.filter((offer) => {
        const key = offer?.id || offer?.packageName || offer?.appName;
        return key ? !removedSet.has(key) : true;
      });
    }
    if (!activeApps.length) return offers;
    return offers.map(markActive);
  }, [activeSegment, offers, activeApps, removedOfferKeys]);

  const activeOfferCount = useMemo(() => {
    if (!activeApps.length) {
      return visibleOffers.filter((offer) => offer?.isActiveOffer).length;
    }
    const removedSet = new Set(removedOfferKeys);
    return activeApps.reduce((count, app) => {
      const key = app?.appName || app?.displayName || app?.packageName || app?.appId || app?.id || app?.name || app?.title;
      if (key && removedSet.has(key)) return count;
      return count + 1;
    }, 0);
  }, [activeApps, removedOfferKeys, visibleOffers]);

  const allOfferCount = useMemo(() => offers.length || 0, [offers]);

  const getActiveStats = (offer) => {
    const toNumber = (value) => {
      if (value === null || value === undefined || value === "") return NaN;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : NaN;
    };
    const readOfferNumber = (key) => {
      const direct = toNumber(offer?.[key]);
      if (Number.isFinite(direct)) return direct;
      const appValue = toNumber(offer?.app?.[key]);
      if (Number.isFinite(appValue)) return appValue;
      const detailsValue = toNumber(offer?.details?.[key]);
      if (Number.isFinite(detailsValue)) return detailsValue;
      return NaN;
    };
    const level = Number(offer?.level ?? 0);
    const safeLevel = Number.isFinite(level) ? level : 0;
    const minutesCompleted = Math.max(0, safeLevel - 1);
    const rewardMeta = offer?.rewardMeta || offer?.app?.rewardMeta || offer?.details?.rewardMeta;
    const registrationCoinsRaw = toNumber(rewardMeta?.registration?.amount ?? offer?.registrationCoins);
    const minuteBaseRaw = toNumber(rewardMeta?.minute?.base);
    const minuteIncrementRaw = toNumber(rewardMeta?.minute?.increment ?? offer?.minuteIncrement);
    const minuteCoinsTotalRaw = readOfferNumber("minuteCoinsTotal");
    let baseRegistrationCoins = Number.isFinite(registrationCoinsRaw) ? registrationCoinsRaw : NaN;
    let increment = Number.isFinite(minuteIncrementRaw) ? minuteIncrementRaw : NaN;
    let minuteBase = Number.isFinite(minuteBaseRaw) ? minuteBaseRaw : NaN;
    if (!Number.isFinite(baseRegistrationCoins)) {
      baseRegistrationCoins = readOfferNumber("registrationCoins");
    }
    if (!Number.isFinite(increment)) {
      increment = readOfferNumber("minuteIncrement");
    }
    if (!Number.isFinite(minuteBase)) {
      minuteBase = baseRegistrationCoins;
    }
    if (!Number.isFinite(baseRegistrationCoins) && Number.isFinite(minuteCoinsTotalRaw) && Number.isFinite(increment)) {
      baseRegistrationCoins = minuteCoinsTotalRaw - increment;
    }
    if (!Number.isFinite(increment) && Number.isFinite(minuteCoinsTotalRaw) && Number.isFinite(baseRegistrationCoins)) {
      increment = minuteCoinsTotalRaw - baseRegistrationCoins;
    }
    if (!Number.isFinite(baseRegistrationCoins)) baseRegistrationCoins = 0;
    if (!Number.isFinite(increment)) increment = 0;
    if (!Number.isFinite(minuteBase)) minuteBase = baseRegistrationCoins;
    const coinsFromMinutes = minutesCompleted * increment;
    const chestStatsTotalRewards = Number(offer?.chestStats?.totalRewards);
    const coinsFromChests = Number.isFinite(chestStatsTotalRewards)
      ? chestStatsTotalRewards
      : Number(offer?.chestCoinsTotal ?? 0) || 0;
    const explicitTotalCoins = Number(offer?.totalCoins ?? offer?.coinsEarned);
    const totalCoins = Number.isFinite(explicitTotalCoins)
      ? explicitTotalCoins
      : baseRegistrationCoins + coinsFromMinutes + coinsFromChests;
    const chestStatsTotalChests = Number(offer?.chestStats?.totalChests);
    const chestsOpened = Number.isFinite(chestStatsTotalChests)
      ? chestStatsTotalChests
      : Number(offer?.chestsOpened ?? Math.floor(minutesCompleted / 30)) || 0;
    return {
      totalCoins,
      minutesCompleted,
      chestsOpened,
      baseRegistrationCoins,
      coinsFromMinutes,
      coinsFromChests,
    };
  };

  const handleSelectOffer = (offer) => {
    if (typeof window !== "undefined") {
      scrollPositionsRef.current[activeSegmentRef.current] = window.scrollY || 0;
      try {
        window.sessionStorage.setItem(
          "mobileOffersScrollPositions",
          JSON.stringify(scrollPositionsRef.current)
        );
      } catch (err) {
        console.warn("mobileOffers scroll persist failed", err);
      }
    }
    setPendingOfferId(offer?.id || offer?.packageName || offer?.appName || "pending");
    const enrichedOffer = {
      ...offer,
      userId: offer.userId || userId || loggedInUser?.userId || loggedInUser?.id,
      packageName:
        offer.packageName || packageName || loggedInUser?.packageName || loggedInUser?.package,
    };
    requestAnimationFrame(() => onSelectOffer(enrichedOffer));
  };

  const closeModal = () => {
    if (typeof window === "undefined") return;
    const androidApp = window.Android;
    androidApp.onUserClose();
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    activeSegmentRef.current = activeSegment;
    hasRestoredScrollRef.current = false;
    const y = scrollPositionsRef.current[activeSegment] || 0;
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: "auto" });
    });
  }, [activeSegment]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.sessionStorage.getItem("mobileOffersScrollPositions");
      if (stored) {
        scrollPositionsRef.current = JSON.parse(stored) || {};
      }
    } catch (err) {
      console.warn("mobileOffers scroll restore failed", err);
    }
    return () => {
      try {
        window.sessionStorage.setItem(
          "mobileOffersScrollPositions",
          JSON.stringify(scrollPositionsRef.current)
        );
      } catch (err) {
        console.warn("mobileOffers scroll persist failed", err);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const notifyAppInForeground = () => {
      window.location.reload();
    };
    window.notifyAppInForeground = notifyAppInForeground;
    return () => {
      if (window.notifyAppInForeground === notifyAppInForeground) {
        delete window.notifyAppInForeground;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loading) return;
    if (hasRestoredScrollRef.current) return;
    const y = scrollPositionsRef.current[activeSegment];
    if (typeof y !== "number") return;
    hasRestoredScrollRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, behavior: "auto" });
      });
    });
  }, [loading, activeSegment, visibleOffers.length]);

  return (
    <div className="m-offers-page">
      {pendingOfferId && (
        <div className="m-offers-loading" role="status" aria-live="polite">
          Opening offer details…
        </div>
      )}
      <div className="m-offers-top">
        <header className="m-offers-header">
          <button className="m-offers-header-btn" onClick={() => {
            if (typeof window !== "undefined") {
              scrollPositionsRef.current = {};
              try {
                window.sessionStorage.setItem(
                  "mobileOffersScrollPositions",
                  JSON.stringify({})
                );
              } catch (err) {
                console.warn("mobileOffers scroll reset failed", err);
              }
              window.scrollTo({ top: 0, behavior: "auto" });
            }
            const search = window.location.search || "";
            window.location.assign(`/${search}`);
          }}>
            <span role="img" aria-label="Home">🏠</span>
          </button>
          <div className="m-offers-header-title">
            <div className="m-offers-header-title-main">{HEADER_TITLE}</div>
          </div>
          <button className="m-offers-header-btn" onClick={() => closeModal()}>
            <span role="img" aria-label="Close">✕</span>
          </button>
        </header>

        {loading && <div className="m-offers-status">Loading offers...</div>}
        {error && !loading && <div className="m-offers-status is-error">{error}</div>}

        <div className="m-offerschips" role="tablist" aria-label="Offer filter">
        {segmentOptions.map(({ id, label }) => {
          const count = id === "active" ? activeOfferCount : allOfferCount;
          return (
          <button
            key={id}
            type="button"
            className={`chip${activeSegment === id ? " chip-active" : ""}`}
            onClick={() => {
              if (typeof window !== "undefined") {
                scrollPositionsRef.current[activeSegment] = window.scrollY || 0;
                try {
                  window.sessionStorage.setItem(
                    "mobileOffersScrollPositions",
                    JSON.stringify(scrollPositionsRef.current)
                  );
                } catch (err) {
                  console.warn("mobileOffers scroll persist failed", err);
                }
              }
              setActiveSegment(id);
            }}
            aria-pressed={activeSegment === id}
          >
            {`${label} (${count})`}
          </button>
          );
        })}
        </div>
      </div>

      <section className="m-offers-list">
        {visibleOffers.map((offer, index) => {
          const activeStats = offer.isActiveOffer ? getActiveStats(offer) : null;
          return (
          <article
            key={offer.id || index}
            className={`m-offer-card${offer.isActiveOffer ? " is-active" : ""}${pendingOfferId && pendingOfferId === (offer.id || offer.packageName || offer.appName) ? " is-loading" : ""}`}
            onClick={() => handleSelectOffer(offer)}
          >
            <div className="m-offer-media">
              <img src={offer.icon || "/default-offer.png"} alt={offer.title || offer.name || "Offer"} loading="lazy" />
            </div>
            <div className="m-offer-body">
              {offer.isActiveOffer ? (
                <div className="m-offer-active-header">
                  <div className="m-offer-active-top">
                    <h3 className="m-offer-title">{offer.title || offer.displayName || "Offer title"}</h3>
                  </div>
                  <div className="m-offer-active-bottom">
                    <span className="m-offer-active-pill">Active</span>
                    <div className="m-offer-active-total">
                      <span>Total coins</span>
                      <strong>
                        {activeStats?.totalCoins ?? 0}
                        <span className="coin-icon" aria-hidden="true" />
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <h3 className="m-offer-title">{offer.title || offer.displayName || "Offer title"}</h3>
              )}
              {offer.isActiveOffer && (
                <div className="m-offer-active-grid">
                  <div className="m-offer-active-stats">
                    <div className="m-offer-active-stat">
                      <span>Minutes spent</span>
                      <strong>{activeStats?.minutesCompleted ?? 0}</strong>
                    </div>
                    <div className="m-offer-active-stat">
                      <span>Chests opened</span>
                      <strong>{activeStats?.chestsOpened ?? 0}</strong>
                    </div>
                  </div>
                  <div className="m-offer-active-cards">
                    <div className="m-offer-active-card">
                      <span className="m-offer-active-card-icon">🧾</span>
                      <span>Registration</span>
                      <strong>
                        {activeStats?.baseRegistrationCoins ?? 0}
                        <span className="coin-icon" aria-hidden="true" />
                      </strong>
                    </div>
                    <div className="m-offer-active-card">
                      <span className="m-offer-active-card-icon">⏱️</span>
                      <span>Minutes</span>
                      <strong>
                        {activeStats?.coinsFromMinutes ?? 0}
                        <span className="coin-icon" aria-hidden="true" />
                      </strong>
                    </div>
                    <div className="m-offer-active-card">
                      <span className="m-offer-active-card-icon">🎁</span>
                      <span>Chests</span>
                      <strong>
                        {activeStats?.coinsFromChests ?? 0}
                        <span className="coin-icon" aria-hidden="true" />
                      </strong>
                    </div>
                  </div>
                </div>
              )}
              {!offer.isActiveOffer && <div className="m-offer-tag">{offer.tagline || "Register Now"}</div>}
              {!offer.isActiveOffer && (
                <p className="m-offer-meta">
                  <span>{offer.duration || "Instant"}</span>
                  <span>{offer.category || CARD_CATEGORY}</span>
                </p>
              )}
              <div className="m-offer-reward">
                <span className="m-reward-pill">
                  <span className="m-reward-inner">
                    {offer.isActiveOffer ? "View Details" : (offer.reward || "10.0Mn Coins")}
                    {!offer.isActiveOffer && <span className="coin-icon" aria-hidden="true" />}
                  </span>
                </span>
              </div>
            </div>
          </article>
          );
        })}
        {!loading && !visibleOffers.length && !error && (
          <div className="m-offers-status">
            {activeSegment === "active" ? "No active offers yet." : "No offers available."}
          </div>
        )}
      </section>
    </div>
  );
}
